import { createClient } from '@supabase/supabase-js';

// VITE_-prefixed env vars are inlined by Vite at build time. If the
// build host (Netlify, etc.) didn't have them set, they end up as
// `undefined` and `createClient(undefined, …)` throws an opaque
// "supabaseUrl is required" deep in the minified Supabase code.
//
// Fail loud and clear instead: throw a message that names the missing
// var and the action needed (set it on the host's env-var screen).
// Same error covers `npm run dev` without a `.env` file.

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

function missingEnvVarMessage(name: string): string {
  return (
    `[QuarterStack] Missing required env var: ${name}. ` +
    `Set it before building / running. For Netlify: Site settings → Environment variables. ` +
    `For local dev: add it to a .env file at the repo root. ` +
    `Example: ${name}=https://your-project.supabase.co (URL) or ${name}=<anon-key> (key).`
  );
}

if (!supabaseUrl) {
  throw new Error(missingEnvVarMessage('VITE_SUPABASE_URL'));
}
if (!supabaseAnonKey) {
  throw new Error(missingEnvVarMessage('VITE_SUPABASE_ANON_KEY'));
}

// The generated DB schema lives in ./database.types.ts (run `bun run gen:types`
// after each migration and commit it); the domain row types in ./types.ts
// alias it, so schema drift on those tables is a compile error. The client is
// intentionally NOT generically typed yet — flipping to createClient<Database>
// type-checks all ~211 call sites at once (jsonb columns, dynamic .from(),
// stricter inserts) and is scoped as its own phase. See the M53 plan.
export const supabase = createClient(supabaseUrl, supabaseAnonKey);
